import BaseDialog from 'common/components/base-dialog';
import React, { useEffect } from 'react';
import FormProvider from 'common/components/form-provider';
import { useForm } from 'react-hook-form';
import { Box, Button, Checkbox, FormControlLabel, Stack } from '@mui/material';
import { createStyleSheet, handleError } from 'utils/helpers';
import {
    listQuestionsForTabulation,
    exportTabulationData,
} from 'services/campaign.service';
import { toast } from 'react-toastify';
import { useParams } from 'react-router-dom';
// import { BaseCheckbox } from 'common/components/base-checkbox-group';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { setIsLoading } from 'slices/loadingSlice';
import { useAppDispatch } from 'common/hook';
import { BaseMultiCheckbox } from 'common/components/base-checkbox-group';

const ModalExportExcelTurbulation: React.FC<{
    open: boolean;
    onClose: () => void;
}> = ({ open, onClose }) => {
    const [listQuestions, setListQuestions] = React.useState<
        {
            id: number;
            title: string;
            kind: string;
            answer_options: {
                id: number | string;
                text: string;
            }[];
            table_question_component: {
                id: number;
                rows: string[];
                columns: string[];
            };
        }[]
    >([]);
    const createValidationSchemaAndDefaults = (
        questions: {
            id: number;
            title: string;
            kind: string;
            answer_options: {
                id: number | string;
                text: string;
            }[];
            table_question_component: {
                id: number;
                rows: string[];
                columns: string[];
            };
        }[]
    ) => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const dynamicShape: Record<string, yup.Schema<any>> = {
            question_ids: yup
                .array()
                .of(yup.number())
                .required('Vui lòng chọn ít nhất một câu hỏi để xuất dữ liệu'),
        };
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const dynamicDefaultValues: Record<string, any> = {}; // Để lưu trữ defaultValues
        questions.forEach((question) => {
            dynamicShape[question.id] = yup.object({
                option_ids: yup.array().of(yup.mixed().nullable().nullable()),
            });
        });
        const validationSchema = yup.object().shape(dynamicShape);
        return { validationSchema, defaultValues: dynamicDefaultValues };
    };
    const { validationSchema, defaultValues } = React.useMemo(() => {
        return createValidationSchemaAndDefaults(listQuestions || []);
    }, [listQuestions]);

    const methods = useForm({
        resolver: yupResolver(validationSchema),
        defaultValues: {},
    });

    useEffect(() => {
        const prevDefaultValues = JSON.stringify(methods.getValues());
        const newDefaultValues = JSON.stringify(defaultValues);

        if (prevDefaultValues !== newDefaultValues) {
            methods.reset(defaultValues);
        }
    }, [defaultValues, methods]);

    const dispatch = useAppDispatch();

    const { handleSubmit, watch } = methods;

    // Gom question_ids thành mảng string đúng cách. Trước đây register() spread
    // thẳng lên MUI Checkbox nên ref rơi vào <span> gốc, nhóm checkbox không tạo
    // được mảng -> question_ids ra boolean/undefined -> yup.array().required()
    // fail và handleSubmit chặn im lặng ("bấm không có gì xảy ra").
    const toggleQuestion = (id: string) => {
        const current: string[] = methods.getValues('question_ids') || [];
        const next = current.includes(id)
            ? current.filter((q) => q !== id)
            : [...current, id];
        methods.setValue('question_ids', next, { shouldValidate: true });
    };

    // Trước đây handleSubmit không có nhánh invalid -> lỗi validation bị nuốt.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const onInvalid = (formErrors: any) => {
        toast.error(
            formErrors?.question_ids?.message ||
                'Vui lòng chọn câu hỏi và đáp án cần xuất'
        );
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const onSubmit = handleSubmit(async (data: any) => {
        try {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const params: {
                question_id: number;
                option_ids: number[];
            }[] = [];
            let errorFound = false;
            data.question_ids.forEach((question_id: string) => {
                const option_ids = data[question_id]?.option_ids || [];
                const findQuestion = listQuestions.find(
                    (question) => question.id === Number(question_id)
                );
                if (
                    findQuestion &&
                    findQuestion.answer_options &&
                    findQuestion.answer_options.length > 0 &&
                    option_ids.length === 0
                ) {
                    errorFound = true;
                    toast.error(
                        `Vui lòng chọn ít nhất một đáp án cho câu hỏi "${findQuestion.title}"`
                    );
                }
                params.push({
                    question_id: Number(question_id),
                    option_ids: option_ids.map((id: string) => id),
                });
            });
            if (errorFound) {
                return;
            }
            if (params.length === 0) {
                errorFound = true;
                toast.error(
                    'Vui lòng chọn ít nhất một câu hỏi để xuất dữ liệu'
                );
                return;
            }
            if (errorFound) {
                return;
            }
            dispatch(setIsLoading(true));
            const response = await exportTabulationData(
                Number(campaignId),
                params
            );
            if (!response.success) {
                toast.error(response.message || 'Lỗi khi xuất dữ liệu');
                dispatch(setIsLoading(false));
                return;
            }
            const link = document.createElement('a');
            link.href = response.data.url;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            setTimeout(() => {
                window.URL.revokeObjectURL(response.data.url);
                dispatch(setIsLoading(false));
                toast.success(
                    'Xuất dữ liệu thành công, vui lòng kiểm tra tệp đã tải xuống'
                );
            }, 100);
            handleClose();
        } catch (error) {
            handleError(error);
            dispatch(setIsLoading(false));
        }
    }, onInvalid);

    const campaignId = useParams<{ id: string }>().id;

    useEffect(() => {
        const fetchQuestions = async () => {
            try {
                const response = await listQuestionsForTabulation(
                    Number(campaignId)
                );
                if (!response.success) {
                    toast.error(response.message || 'Lỗi khi tải câu hỏi');
                }
                setListQuestions(response.data);
            } catch (error) {
                handleError(error);
            }
        };
        if (open) {
            fetchQuestions();
        }
    }, [open, campaignId]);

    const handleClose = () => {
        onClose();
        methods.reset();
    };

    const question_ids = watch('question_ids') || [];

    return (
        <BaseDialog
            open={open}
            onClose={handleClose}
            title="Xuất dữ liệu tabulation"
            fullWidth={false}
            maxWidth={false}
            sxContent={{
                width: '650px',
                padding: '40px',
            }}
        >
            <FormProvider methods={methods} onSubmit={onSubmit}>
                <Stack
                    mt={2}
                    spacing={2}
                    direction="column"
                    justifyContent="center"
                >
                    <Stack
                        direction="column"
                        spacing={2}
                        sx={{ width: '100%' }}
                    >
                        {listQuestions.map((question) => (
                            <Box key={question.id}>
                                <FormControlLabel
                                    key={question.id}
                                    control={
                                        <Checkbox
                                            checked={question_ids.includes(
                                                question.id.toString()
                                            )}
                                            onChange={() =>
                                                toggleQuestion(
                                                    question.id.toString()
                                                )
                                            }
                                            color="primary"
                                        />
                                    }
                                    label={question.title}
                                />
                                {question_ids &&
                                    question_ids?.includes(
                                        question.id.toString()
                                    ) && (
                                        <BaseMultiCheckbox
                                            name={`${question.id}.option_ids`}
                                            options={question.answer_options.map(
                                                (option) => ({
                                                    label: option.text,
                                                    value: option.id,
                                                })
                                            )}
                                            sx={{ paddingLeft: '20px' }}
                                        />
                                    )}
                            </Box>
                        ))}
                    </Stack>
                    <Button
                        type="submit"
                        sx={{
                            ...styles.btn,
                            color: (theme) => theme.palette.common.white,
                        }}
                        variant="contained"
                    >
                        Xuất dữ liệu
                    </Button>
                </Stack>
            </FormProvider>
        </BaseDialog>
    );
};
const styles = createStyleSheet({
    btn: {
        textTransform: 'none',
        borderRadius: '40px',
        minWidth: '116px',
        minHeight: '60px',
        fontSize: '20px',
        fontWeight: '600',
    },
});

export default ModalExportExcelTurbulation;
